import { useEffect, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormField,
  FormMessage,
  FormDescription,
  Input,
  SingleSelect,
  Alert,
  AlertTitle,
  AlertDescription,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@predoc/cow';
import { Info, AlertCircle } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateForm } from '@/hooks';
import { format, parseISO } from 'date-fns';
import { ExistingPatient } from '../types';
import { GENDER_OPTIONS } from '../constants';
import {
  patientSchema,
  PatientFormFieldProps,
  NestedPatientFormFieldProps,
  PatientFormType,
} from './request-steps/patient/validation';
import { ProfileSingleSelectDropdown } from '@/feature/users/components/ProfileAccessDropdown';
import { useGetCurrentUserProfileAccess } from '@/feature/users/hooks/usersApi';
import { DatePickerInput } from '@/components/DatePickerInput';
import { formatPhoneNumber, parsePhoneNumber } from '../utils/phoneUtils';
import {
  areAllRequestsOpen,
  hasCombinationOfStatuses,
} from '../utils/requestStatusUtils';
import { useUpdatePatientDemographics } from '../hooks/patientsAPI';

interface EditPatientModalProps {
  patient: ExistingPatient;
  isOpen: boolean;
  onClose: () => void;
}

export const EditPatientModal = ({
  patient,
  isOpen,
  onClose,
}: EditPatientModalProps) => {
  const { data: profileOptions = [] } = useGetCurrentUserProfileAccess({
    enabled: isOpen,
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const updatePatientDemographics = useUpdatePatientDemographics();

  const form = useUpdateForm<PatientFormType>({
    resolver: zodResolver(patientSchema),
    mode: 'onSubmit',
  });

  // Watch the organizationProfile field to trigger re-renders on change
  const watchedOrganizationProfile = form.watch('organizationProfile');

  // Show profile change alert when user selects a different profile
  const showProfileChangeAlert =
    watchedOrganizationProfile !== patient.organizationProfile.id;

  useEffect(() => {
    if (isOpen) {
      const formData: PatientFormType = {
        ...patient,
        genderName: patient.genderName as PatientFormType['genderName'],
        organizationProfile: patient.organizationProfile.id,
      };
      form.reset(formData);
      setApiError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, patient]);

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const handleProfileChange = useCallback(
    (profileId: string, onChange: (value: string) => void) => {
      onChange(profileId);
    },
    [],
  );

  const handleSubmit = async (dirtyFields: Partial<PatientFormType>) => {
    try {
      setApiError(null);
      // Rename organizationProfile to organizationProfileId for API, spread the rest
      const { organizationProfile, ...rest } = dirtyFields;
      await updatePatientDemographics.mutateAsync({
        id: patient.id,
        ...rest,
        ...(organizationProfile && {
          organizationProfileId: organizationProfile,
        }),
      });
      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update patient. Please try again.';
      setApiError(errorMessage);
    }
  };

  // Check request statuses
  const patientRequestStatuses = patient.organizationStatus || {};
  const allRequestsOpen = areAllRequestsOpen(patientRequestStatuses);
  const hasCombination = hasCombinationOfStatuses(patientRequestStatuses);

  // If all requests are open: all fields editable
  // If not all open AND has combination: disable all fields
  // Otherwise (not all open but same category): only disable profile selector
  const isAllFieldsDisabled = !allRequestsOpen && hasCombination;
  const isProfileDisabled = !allRequestsOpen;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="mx-auto !max-w-[946px]">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-semibold">
            Edit Patient Details
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleUpdateSubmit(handleSubmit)}>
            <div className="space-y-6">
              <Alert variant="accent" className="mb-6">
                <Info className="size-4" />
                <AlertDescription>
                  Patient details can only be edited before a request begins
                  processing. After processing begins please contact your
                  administrator for assistance.
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }: PatientFormFieldProps<'firstName'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Janet"
                          aria-label="First Name Input"
                          disabled={isAllFieldsDisabled}
                          maxLength={45}
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value?.length || 0}/45 characters
                      </FormDescription>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="middleName"
                  render={({ field }: PatientFormFieldProps<'middleName'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>Middle Initial or Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. M"
                          aria-label="Middle Initial or Name Input"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormDescription className="invisible">
                        &nbsp;
                      </FormDescription>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }: PatientFormFieldProps<'lastName'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel className="flex items-center justify-between">
                        Last Name *
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="text-muted-foreground flex items-center gap-1 text-sm underline hover:cursor-pointer">
                              <Info className="h-4 w-4" />
                              former last names
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            Include former last names in the Last Name field by
                            using parentheses and commas. e.g. &quot;Doe
                            (Washington, Avery)&quot;
                          </TooltipContent>
                        </Tooltip>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Doe"
                          aria-label="Last Name Input"
                          disabled={isAllFieldsDisabled}
                          maxLength={60}
                        />
                      </FormControl>
                      <FormDescription>
                        {field.value?.length || 0}/60 characters
                      </FormDescription>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }: PatientFormFieldProps<'dateOfBirth'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Date of Birth *</FormLabel>
                      <FormControl>
                        <DatePickerInput
                          onChange={(date: Date | undefined) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : '',
                            );
                          }}
                          value={
                            field.value ? parseISO(field.value) : undefined
                          }
                          disabled={isAllFieldsDisabled}
                          disableDates="future"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="genderName"
                  render={({ field }: PatientFormFieldProps<'genderName'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Gender *</FormLabel>
                      <FormControl>
                        <SingleSelect
                          options={GENDER_OPTIONS}
                          handleChange={field.onChange}
                          value={field.value}
                          placeholder="Select a gender..."
                          className="w-full"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="contact.phoneNumber"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'contact.phoneNumber'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Phone Number *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          placeholder="ex. (123) 456-7890"
                          maxLength={14}
                          value={formatPhoneNumber(field.value || '')}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (!isAllFieldsDisabled) {
                              const formatted = formatPhoneNumber(
                                e.target.value,
                              );
                              const numeric = parsePhoneNumber(formatted);
                              field.onChange(numeric);
                            }
                          }}
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact.emailAddress"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'contact.emailAddress'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="ex. janetdoe@email.com"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="address.address1"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.address1'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Street Address</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. 123 Maple Avenue"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="address.address2"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.address2'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Street Address Line 2</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Unit 100"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="address.city"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.city'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. Greenville"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.state"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.state'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>State/Province</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. NY"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address.postalCode"
                  render={({
                    field,
                  }: NestedPatientFormFieldProps<'address.postalCode'>) => (
                    <FormItem className="col-span-4">
                      <FormLabel>Postal/ZIP Code *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. 12345"
                          disabled={isAllFieldsDisabled}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="organizationProfile"
                  render={({
                    field,
                  }: PatientFormFieldProps<'organizationProfile'>) => {
                    return (
                      <FormItem className="col-span-6">
                        <FormLabel>Profile *</FormLabel>
                        <FormControl>
                          <ProfileSingleSelectDropdown
                            value={field.value || ''}
                            onChange={(profileId: string) =>
                              handleProfileChange(profileId, field.onChange)
                            }
                            disabled={isProfileDisabled}
                            options={profileOptions}
                          />
                        </FormControl>
                        <FormMessage className="text-destructive" />
                      </FormItem>
                    );
                  }}
                />
              </div>
              {showProfileChangeAlert && (
                <Alert variant="accent" className="mb-6">
                  <Info className="size-4" />
                  <AlertTitle>Profile Change Notice</AlertTitle>
                  <AlertDescription>
                    You&apos;re about to move this patient to another profile.
                    Only users assigned to the new profile will have access to
                    this patient.
                  </AlertDescription>
                </Alert>
              )}
              {apiError && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{apiError}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="mt-6 border-t pt-4">
              <div className="flex w-full items-center justify-end gap-4">
                {form.formState.isSubmitted && !form.formState.isValid ? (
                  <div className="text-destructive flex items-center gap-2 text-sm">
                    <AlertCircle className="size-4" />
                    Please fix validation errors.
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    aria-label="Save"
                    disabled={isAllFieldsDisabled || !form.formState.isDirty}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
